import { ChannelDestination } from 'communication/Channel';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import { SignalTileStreamRequest } from 'widgets/signalTiles/signalTile/api/SignalTileStreamRequest';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { SymbolTilesStreamRequest } from 'communication/types/symbolTiles/SymbolTilesStreamRequest';
import GetOrderColumnsRequest from 'communication/types/order/GetOrderColumnsRequest';
import { LiquidityStreamRequest } from 'widgets/liquiditySummary/api/LiquiditySummaryStreamRequest';
import FillsGraphByOrderIdOrSymQtyStreamRequest from 'communication/types/fillsGraph/FillsGraphByOrderIdOrSymQtyStreamRequest';
import { ExpertiseStreamRequest } from 'communication/types/expertise/ExpertiseStreamRequest';
import { IntraDayProfileStreamRequest } from 'communication/types/intraDayProfile/IntraDayProfileStreamRequest';
import { PortfolioPlStreamRequest } from 'communication/types/portfolioPl/PortfolioPlStreamRequest';
import { PortfolioReturnsStreamRequest } from 'communication/types/portfolioReturns/PortfolioReturnsStreamRequest';
import { CandlestickTileStreamRequest } from 'widgets/candlestickTile/api/CandlestickTileStreamRequest';
import { OrderIdsStreamRequest } from 'communication/types/order/OrderIdsStreamRequest';
import { GetSymbolInfoRequestPayload } from 'widgets/symbolInfo/SymbolInfoRequest';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRequest = any | StreamError;

const symbology = SymbologyValues.Bloomberg;
const symbol = 'VOD LN';

export function getTemplateRequest(
    channelDestination: ChannelDestination
): AnyRequest {
    switch (channelDestination) {
        case 'startPfeStream':
        case 'startRsiStream':
            return getTemplateSignalRequest();
        case 'startSymbolTilesStream':
            return getSymbolTileRequest();
        case 'startOrderColumnsStream':
            return getOrderColsRequest();
        case 'startLiquidityStream':
            return getLiquidityTemplate();
        case 'startFillsGraphByOrderIdOrSymQtyStream':
            return getFillsGraphByOrderIdOrSymQtyStreamRequest();
        case 'startExpertiseByOrderIdsStream':
            return getExpertiseByOrderIdsStream();
        case 'startIntradayProfilesStream':
            return getIntradayProfilesStream();
        case 'startPLGraphStream':
            return getPLGraphStream();
        case 'startPortfolioReturnsStream':
            return getPortfolioReturnsStream();
        case 'startPriceChartStream':
            return getPriceChartStream();
        case 'startStrategyParamsCurByOrderIdsStream':
        case 'startStrategyParamsHistByOrderIdsStream':
        case 'startVenueAnalysisByOrderIdsStream':
        case 'startOpenSubmissionsByOrderIdsStream':
            return getOrderIdsStreamRequest();
        case 'symbolInfo':
            return getSymbolInfoRequest();
        case 'startParmUpdates':
        case 'INIT':
        case 'renew':
        case 'resend':
            return undefined;
    }
}

function getTemplateSignalRequest(): SignalTileStreamRequest {
    return {
        numWindows: 14,
        symbol,
        symbology,
    };
}

function getSymbolTileRequest(): SymbolTilesStreamRequest {
    return {
        symbology,
        addSyms: 'VOD LN|BARC LN',
        delSyms: '',
    };
}

function getOrderColsRequest(): GetOrderColumnsRequest {
    return {
        addCols: [
            'avgPx',
            'bloom',
            'cusip',
            'fillQty',
            'fillT',
            'idx',
            'isymbol',
            'localSymC',
            'orderID',
            'qty',
            'rem',
            'sedol',
            'side',
            'state',
        ],
        delCols: [],
    };
}

function getLiquidityTemplate(): LiquidityStreamRequest {
    return {
        symbol,
        qty: 100,
        symbology,
    };
}

function getFillsGraphByOrderIdOrSymQtyStreamRequest(): FillsGraphByOrderIdOrSymQtyStreamRequest {
    return {
        orderIds: '2210708000000010',
        indexSymbology: 'Bloomberg',
        indexSym: '',
        extendedHours: false,
        useSecondarySymbol: true,
        normalizeBy: 101,

        includeStrategyParms: true,
        useIndividualOrderArrival: true,
        benchmarkType: 'ARRIVAL',
    };
}

function getExpertiseByOrderIdsStream(): ExpertiseStreamRequest {
    return {
        orderIds: '2210708000000010',
        reportType: 'DFAMake',
    };
}

function getIntradayProfilesStream(): IntraDayProfileStreamRequest {
    return {
        symbology,
        bucketSize: 1,
        symQty: 'VOD LN,5089|GBF GR,5089|GSK LN,12089',
    };
}

function getPLGraphStream(): PortfolioPlStreamRequest {
    return {
        benchmarkType: 'ARRIVAL',
        indexSymbol: symbol,
        indexSymbology: symbology,
        orderIds: '2210708000000010',
        useIndividualOrderArrival: true,
        currency: 'USD',
    };
}

function getPortfolioReturnsStream(): PortfolioReturnsStreamRequest {
    return {
        symQty: '`BARC LN,79848610|VOD LN,57047943|EPR IM,11818247````',
        symbology:
            'Bloomberg`Bloomberg`Bloomberg`Bloomberg`Bloomberg`Bloomberg',
        days: 1,
        toDate: null,
        fromDate: null,
        barSize: 0,
    };
}

function getPriceChartStream(): CandlestickTileStreamRequest {
    return {
        bucketSize: 5,
        symbol,
        symbology,
    };
}

function getOrderIdsStreamRequest(): OrderIdsStreamRequest {
    return {
        orderIds: '2210708000000010',
    };
}

function getSymbolInfoRequest(): GetSymbolInfoRequestPayload {
    return {
        symbol,
        symbology,
    };
}
